import { Button, Cell, Divider, Grid, PersonBlock, Radio, RadioGroup, Separator, TextField, H5 } from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getMembershipSearchResults } from '../../../services/membershipService'
import { isNotNullOrUndefinedOrEmpty } from '../../../util/Utils'
import { MrnLink } from '../../app/Links'
import { DmcDatePicker } from '../../common/DmcDatePicker/DmcDatePicker'

export default function MembershipSearch({ history, location }) {
  usePageView({ page_detail: 'DMConsole:MembershipSearch' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.search.membership'))
  // default values if available
  const searchParams = new URLSearchParams(location.search)
  const birthDateParam = searchParams.get('birthDate') || searchParams.get('birthYear')
  const sexParam = searchParams.get('sex')
  const nameParam = searchParams.get('name')

  // Other hooks
  const { error } = useNotifications()

  // Inputs state
  const [name, setName] = useState(nameParam)
  const [datePickerItem, setDatePickerItem] = useState()
  const [sex, setSex] = useState(sexParam || 'unknown')

  // Results state
  const [searchResults, setSearchResults] = useState()

  // on first load
  useEffect(() => {
    if (isNotNullOrUndefinedOrEmpty(name)) {
      getMembershipSearchResults(name, datePickerItem?.formattedDate || birthDateParam, sex)
        .then((response) => {
          setSearchResults(response.data.results)
        })
        .catch((err) => {
          if (err.message.includes('404')) {
            // MJV: we should really return the status code from the server and not an error
            // Don't show an error in case of 404
          } else {
            error(
              t('search.errors.gettingMembership', {
                defaultValue: `Error getting membership search results: ${err.message || ''}`,
                message: err.message,
              }),
              err
            )
          }
        })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, birthDateParam])

  // functions
  const handleFormSubmit = (event) => {
    event.preventDefault()
    let queryString = `name=${name}`
    if (datePickerItem?.formattedDate) {
      queryString += `&birthDate=${datePickerItem.formattedDate}`
    }
    if (sex && (sex === 'male' || sex === 'female')) {
      queryString += `&sex=${sex}`
    }
    history.push(`/search/membership?${queryString}`)
  }

  // UI
  return (
    <>
      <form onSubmit={(event) => handleFormSubmit(event)}>
        <Grid>
          <Cell columns={3}>
            <TextField
              id="nameInput"
              label={t('name', 'Name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </Cell>
          <Cell columns={3}>
            <DmcDatePicker
              initialTextboxFormattedDate={birthDateParam}
              selectedItem={datePickerItem}
              setSelectedItem={setDatePickerItem}
              label={t('date', 'Date')}
              dayMonthYearDateFormat="yyyyMMDD"
              monthYearDateFormat="yyyy"
              yearDateFormat="yyyy"
            />
          </Cell>
        </Grid>
        <MembershipSearchInputRow label={t('sex', 'Sex')}>
          <RadioGroup value={sex} onChange={(event) => setSex(event.target.value)}>
            <Radio
              name={t('gender.unknown', 'Unknown')}
              checked={sex === 'unknown'}
              label={t('gender.unknown', 'Unknown')}
              value="unknown"
              onChange={() => setSex('unknown')}
              color="primary"
            />
            <Radio
              name={t('gender.male', 'Male')}
              checked={sex === 'male'}
              label={t('gender.male', 'Male')}
              value="male"
              onChange={() => setSex('male')}
              color="primary"
            />
            <Radio
              name={t('gender.female', 'Female')}
              checked={sex === 'female'}
              label={t('gender.female', 'Female')}
              value="female"
              onChange={() => setSex('female')}
              color="primary"
            />
          </RadioGroup>
        </MembershipSearchInputRow>
        <Button type="submit" emphasis="high" disabled={!name || name === ''}>
          {t('action.search', 'Search')}
        </Button>
      </form>
      <Separator size="xs" />
      {searchResults && searchResults.length >= 0 && (
        <>
          <h4>
            {t(
              searchResults.length === 0 || searchResults.length > 1
                ? 'membership.search.matchesFound_plural'
                : 'membership.search.matchesFound',
              `${searchResults.length} Matches Found`,
              {
                count: searchResults.length,
              }
            )}
          </h4>
          {searchResults.map((result, index) => (
            <React.Fragment key={result.uuid}>
              <MembershipSearchResult data={result} />
              {index !== searchResults.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </>
      )}
      {(searchResults === null || searchResults?.length === 0) && <H5>{t('noResults', 'No Results Found')}</H5>}
    </>
  )
}

function MembershipSearchInputRow({ label, children }) {
  return (
    <Cell lg="4" md="5" sm="8" xs="12">
      {label}
      {children}
    </Cell>
  )
}

function MembershipSearchDetail({ names, birthDate, livingString }) {
  return (
    <div>
      Birth Date: {birthDate} • Living: {livingString}
      <br />
      {names.map((name) => {
        return (
          <span key={`${name}${birthDate}${livingString}`}>
            <strong>{name.type}</strong>: {name.fullName}{' '}
          </span>
        )
      })}
    </div>
  )
}

function MembershipSearchResult({ data }) {
  const { t } = useTranslation()
  const livingString = data.living ? t('boolean.true', 'True') : t('boolean.false', 'False')

  return (
    <PersonBlock
      size="md"
      avatarProps={{
        sex: data.sex.toLowerCase(),
      }}
      name={<MrnLink mrn={data.mrn} mrnText={data.names[0].fullName} />}
      details={<MembershipSearchDetail names={data.names} birthDate={data.birthDate} livingString={livingString} />}
    />
  )
}
